import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { UserPlus, Mail, Lock, X, User } from 'lucide-react';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

// ── Design tokens (matches Leads / Members) ──────────────────────────────────
const C = {
  teal: '#115550',
  tealMid: '#16706a',
  tealLight: '#c6e3e0',
  lime: '#c8e94b',
  limeDim: 'rgba(200,233,75,0.18)',
  limeBorder: 'rgba(200,233,75,0.4)',
  white: '#ffffff',
  muted: '#9ca3af',
  sub: '#6b7280',
};
const G = {
  shellBg: '#f8f9f9',
  shellBorder: '#e8ebeb',
  line: '#e5e7eb',
  soft: '#eef0f0',
};
const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

// ── Types ─────────────────────────────────────────────────────────────────────
interface AddStaffFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/** Matches POST /api/v1/staff body (role is always "staff" on the server) */
interface StaffFormData {
  email: string;
  password: string;
  name: string;
}

const EMPTY: StaffFormData = {
  email: '',
  password: '',
  name: '',
};

// ── Small building blocks ────────────────────────────────────────────────────
const iconBox = (size = 29.5): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: '7.4px',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: C.lime,
});

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '9.2px',
      marginBottom: '13px',
      paddingBottom: '11px',
      borderBottom: `1px solid ${G.soft}`,
    }}
  >
    <div style={iconBox()}>{icon}</div>
    <span
      style={{
        color: C.teal,
        fontWeight: 700,
        fontSize: '0.71rem',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
      }}
    >
      {title}
    </span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const AddStaffForm: React.FC<AddStaffFormProps> = ({ open, onClose, onSuccess }) => {
  const toast = useRef<Toast>(null);

  const [formData, setFormData] = useState<StaffFormData>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open) setFormData(EMPTY);
  }, [open]);

  const update =
    (key: keyof StaffFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const handleClose = () => {
    if (loading) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  const showError = (detail: string, summary = 'Validation Error') =>
    toast.current?.show({ severity: 'error', summary, detail, life: 5000 });

  const handleSubmit = async () => {
    const email = formData.email.trim();
    if (!email) return showError('Email is required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return showError('Enter a valid email address');
    if (!formData.password) return showError('Password is required');
    // Backend binding: min=6
    if (formData.password.length < 6) return showError('Password must be at least 6 characters');

    setLoading(true);
    try {
      // POST /api/v1/staff — role is forced to "staff" by the server
      // Agency is taken from JWT for admins; superadmins must use another path if needed
      const payload: Record<string, string> = {
        email,
        password: formData.password,
      };
      const name = formData.name.trim();
      if (name) payload.name = name;

      await apiClient.post('/v1/staff', payload);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Member added successfully!',
        life: 4000,
      });
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 300);
    } catch (e: any) {
      showError(
        e.response?.data?.error || e.response?.data?.message || 'Failed to add member',
        'Error'
      );
    } finally {
      setLoading(false);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          ...iconBox(44),
          borderRadius: '12px',
          boxShadow: '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        <UserPlus size={20} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: '1.01rem',
            fontWeight: 700,
            color: '#111827',
            lineHeight: 1.2,
            marginBottom: '3px',
          }}
        >
          Add New Member
        </div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>
          Create a staff account (role is always staff)
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        className={`staff-dialog ${isClosing ? 'staff-dialog-closing' : ''}`}
        maskClassName={`staff-mask ${isClosing ? 'staff-mask-closing' : ''}`}
        style={{ width: '90vw', maxWidth: '520px' }}
        modal
        dismissableMask={!loading}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .staff-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; animation: staffMaskIn 0.3s ease; }
          .staff-mask-closing { animation: staffMaskOut 0.3s ease forwards !important; }
          .staff-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; animation: staffDlgIn 0.3s cubic-bezier(0.4,0,0.2,1); }
          .staff-dialog-closing.p-dialog { animation: staffDlgOut 0.3s cubic-bezier(0.4,0,0.2,1) forwards !important; }
          .staff-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
          .staff-dialog .p-dialog-header-icon { color: ${C.sub}; }
          .staff-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; }
          @keyframes staffMaskIn  { from { opacity: 0; } to { opacity: 1; } }
          @keyframes staffMaskOut { from { opacity: 1; } to { opacity: 0; } }
          @keyframes staffDlgIn  { from { opacity: 0; transform: scale(0.94) translateY(-14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          @keyframes staffDlgOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.92) translateY(16px); } }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
          {/* ── Account ─────────────────────────────────────────────── */}
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '16px' }}>
              <SectionHeader icon={<Mail size={14} />} title="Account Details" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
                <GlobalInputField
                  label="Name"
                  type="text"
                  icon={User}
                  value={formData.name}
                  onChange={update('name')}
                  placeholder="Display name (optional)"
                  disabled={loading}
                  autoComplete="off"
                />
                <GlobalInputField
                  label="Email"
                  required
                  type="email"
                  icon={Mail}
                  value={formData.email}
                  onChange={update('email')}
                  placeholder="name@example.com"
                  disabled={loading}
                  autoComplete="off"
                />
                <GlobalInputField
                  label="Password"
                  required
                  type="password"
                  icon={Lock}
                  value={formData.password}
                  onChange={update('password')}
                  placeholder="Minimum 6 characters"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>

          {/* ── Actions ─────────────────────────────────────────────── */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '7.4px',
              marginTop: '2px',
            }}
          >
            <GlobalButton
              label="Cancel"
              variant="secondary"
              icon={X}
              onClick={handleClose}
              disabled={loading}
            />
            <GlobalButton
              label={loading ? 'ADDING...' : 'ADD MEMBER'}
              variant="primary"
              icon={UserPlus}
              onClick={handleSubmit}
              disabled={loading}
              loading={loading}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddStaffForm;